import { createSelector, createReducer } from '@reduxjs/toolkit';
import { USERCAPACITY_INCLUDE } from '../../constants/api';
import {
  getAllHearingsSuccess,
  getAllUsersSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
} from '../actions/api';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';

import { updateDtoList } from '../helpers/reducerHelpers';
import {
  getIncludedData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';

// -------- Overview --------

// UserCapacity is included in the following endpoints
//  - GET /user
//  - GET /user/{id}

// -------- Reducer --------
const userCapacityReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllUsersSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userCapacity = data.filter(
          (include) => include.type === USERCAPACITY_INCLUDE,
        );
        return updateDtoList(state, 'all', userCapacity);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userCapacities = data.filter(
          (include) => include.type === USERCAPACITY_INCLUDE,
        );
        return updateDtoList(state, 'all', userCapacities);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userCapacities = data.filter(
          (include) => include.type === USERCAPACITY_INCLUDE,
        );
        return updateDtoList(state, 'all', userCapacities);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userCapacities = data.filter(
          (include) => include.type === USERCAPACITY_INCLUDE,
        );
        return updateDtoList(state, 'all', userCapacities);
      });
  },
);

// -------- Selectors --------
const selectGetAllUserCapacities = (state) =>
  selectAllFromApiState(state, 'userCapacity');
const allUserCapacitiesSelector = createSelector(
  selectGetAllUserCapacities,
  (userCapacities) => userCapacities,
);
const userCapacityByCapacitySelector = (capacity) =>
  createSelector(selectGetAllUserCapacities, (userCapacities) =>
    userCapacities.find(
      (userCapacity) => getAttr(userCapacity, 'capacity') === capacity,
    ),
  );

const userCapacityByUserSelector = (user) =>
  createSelector(selectGetAllUserCapacities, (userCapacities) =>
    userCapacities.find(
      (userCapacity) =>
        getEntityId(userCapacity) === getRelId(user, 'userCapacity'),
    ),
  );

const userHasCapacitySelector = (user, capacity) =>
  createSelector(
    [
      userCapacityByCapacitySelector(capacity),
      userCapacityByUserSelector(user),
    ],
    (userCapacity, userUserCapacity) => {
      const userCapacityId = getEntityId(userCapacity);
      const userUserCapacityId = getEntityId(userUserCapacity);
      if (!userCapacityId || !userUserCapacityId) {
        return false;
      }
      return userCapacityId === userUserCapacityId;
    },
  );

// -------- Exporting --------
export {
  allUserCapacitiesSelector,
  userCapacityByCapacitySelector,
  userCapacityByUserSelector,
  userHasCapacitySelector,
};
export default userCapacityReducer;
